<template>
    <div class="work-order-form">
        <!-- Main Form Tabs -->
        <!-- Deleted Record Toolbar -->
        <div v-if="model.IsDeleted" class="alert alert-danger d-flex align-items-center justify-content-between mb-2 py-2">
            <span><strong>This record has been deleted.</strong> You can restore it to make it active again.</span>
            <button 
                @click="restoreRecord()" 
                class="btn btn-sm btn-danger"
                type="button"
            >
                <RotateCcw :size="14" class="me-1" />
                Restore Record
            </button>
        </div>

        <div class="d-flex align-items-center">
            <ul class="nav nav-tabs flex-grow-1" role="tablist">
                <li class="nav-item" role="presentation">
                    <button 
                        class="nav-link" 
                        :class="{ active: activeFormTab === 'header' }"
                        @click="activeFormTab = 'header'"
                        type="button"
                    >
                        <span class="badge" :class="activeFormTab === 'header' ? 'tab-badge-active' : 'tab-badge'">
                            <ClipboardList :size="16" class="me-1" style="vertical-align: text-bottom;" />
                            Request Details
                        </span>
                    </button>
                </li>
                <li class="nav-item" role="presentation">
                    <button 
                        class="nav-link" 
                        :class="{ active: activeFormTab === 'billing' }"
                        @click="activeFormTab = 'billing'"
                        type="button"
                    >
                        <span class="badge" :class="activeFormTab === 'billing' ? 'tab-badge-active' : 'tab-badge'">
                            <DollarSign :size="16" class="me-1" style="vertical-align: text-bottom;" />
                            Billing & Payment
                        </span>
                    </button>
                </li>
            </ul>
            <button 
                v-if="model.Id"
                @click="printInvoice()" 
                class="btn btn-sm btn-outline-info ms-2 mb-1"
                title="Print Invoice"
                type="button"
            >
                <i class="fa fa-print"></i> Print 
            </button>
            <button 
                v-if="model.Id && model.Status !== 5"
                @click="openFinalizeModal()" 
                class="btn btn-sm btn-outline-warning ms-2 mb-1"
                title="Finalize Work Order"
                type="button"
            >
                <i class="fa fa-check-circle"></i> Finalize 
            </button>
            <button 
                v-if="model.Id"
                @click="openSendEmail()" 
                class="btn btn-sm btn-outline-success ms-2 mb-1"
                title="Send Email"
                type="button"
            >
                <i class="fa fa-paper-plane"></i> Send 
            </button>
        </div>

        <div class="tab-content border border-top-0 p-3 bg-light">
            <!-- Request Details Tab -->
            <div v-show="activeFormTab === 'header'">
                <div class="row">
                    <div class="col-md-6">

                        <div class="row mb-2">
                            <div class="col-md-6">
                                <kt-select
                                    label="Type"
                                    v-model="model.Title"
                                    :options="titleOptions"
                                ></kt-select>
                            </div>
                            <div class="col-md-6">
                                <label class="d-block">
                                    <strong>Client</strong>
                                </label>
                                <client-ac 
                                    v-model="model.ContactId" 
                                    input-class="form-control form-control-sm" 
                                ></client-ac>
                            </div>
                        </div>

                        <label><strong>Internal Notes</strong></label>
                        <textarea 
                            class="form-control" 
                            v-model="model.InternalNotes"
                            rows="8"
                            placeholder="Internal notes (not visible to client)"
                        ></textarea>
                    </div>
                    <div class="col-md-6">
                        

                        <div class="row mt-3">
                            <div class="col-md-12">
                                <label class="d-block" v-if="showJobField || model.RequestId">
                                    <strong>Related Work Order</strong>
                                </label>
                                <div v-if="showJobField || model.RequestId" class="d-flex align-items-center gap-2">
                                    <div class="flex-grow-1">
                                    <job-ac 
                                        v-model="model.RequestId" 
                                        :client="model.ContactId"
                                        input-class="form-control form-control-sm" 
                                    ></job-ac>
                                    </div>

                                    <button 
                                        v-if="!model.RequestId"
                                        type="button" 
                                        class="btn btn-sm btn-outline-secondary"
                                        @click="showJobField = false"
                                        title="Hide work order selector"
                                    >
                                        <span>&times;</span>
                                    </button>
                                </div>
                                <div v-else class="d-flex gap-2">
                                    <button 
                                        type="button"
                                        class="btn btn-sm btn-outline-secondary"
                                        @click="showJobField = true"
                                    >
                                        <i class="fas fa-link"></i> Link related Work Order
                                    </button>
                                    <!-- 
                                    <button 
                                        v-if="model.Id"
                                        type="button"
                                        class="btn btn-sm btn-outline-primary"
                                        @click="createWorkOrder"
                                    >
                                        <i class="fas fa-plus"></i> Create Work Order
                                    </button>
                                    -->
                                </div>
                            </div>
                        </div>

                        <!-- Related Request selector (for Invoice/Partial types) -->
                        <div class="row mt-3" v-if="isInvoiceOrPartial && model.RequestId">
                            <div class="col-md-12">
                                <label><strong>Related Request</strong></label>
                                <select 
                                    class="form-select form-select-sm"
                                    v-model="model.RelatedWorkOrderId"
                                >
                                    <option :value="null">-- None --</option>
                                    <option 
                                        v-for="req in relatedRequests" 
                                        :key="req.Id" 
                                        :value="req.Id"
                                    >
                                        #{{ req.Id }} - {{ req.RequestTitle || req.Description || 'Untitled' }}
                                        <template v-if="req.Status === 3"> (Completed)</template>
                                    </option>
                                </select>
                                <small class="text-muted">When this invoice is completed, the linked request will be automatically marked complete.</small>
                            </div>
                        </div>

                        <div class="row mt-3">
                            <div class="col-md-6">
                                <kt-input
                                    type="text"
                                    label="Invoice Number" 
                                    input-class="form-control form-control-sm"
                                    v-model="model.InvoiceNumber"
                                    placeholder="Auto-generated if blank"
                                ></kt-input>
                            </div>
                            <div class="col-md-6">
                                <kt-input
                                    type="text"
                                    label="PO Number" 
                                    input-class="form-control form-control-sm"
                                    v-model="model.PONumber"
                                    placeholder="Purchase Order #"
                                ></kt-input>
                            </div>
                        </div>

                        <div class="row mt-3">
                            <div class="col-md-6">
                                <kt-select
                                    label="Status"
                                    v-model="model.Status"
                                    :options="statusOptions"
                                ></kt-select>
                            </div>
                            <div class="col-md-6">
                                <kt-select
                                    label="Payment Status"
                                    v-model="model.PaymentStatus"
                                    :options="paymentStatusOptions"
                                ></kt-select>
                            </div>
                        </div>

                        <div class="row mt-3">
                            <div class="col-md-4">
                                <kt-input
                                    type="date"
                                    label="Date Created" 
                                    input-class="form-control form-control-sm"
                                    v-model="dateCreated"
                                ></kt-input>
                            </div>
                            <div class="col-md-4">
                                <kt-input
                                    type="date"
                                    label="Due Date" 
                                    input-class="form-control form-control-sm"
                                    v-model="dateDue"
                                ></kt-input>
                            </div>
                            <div class="col-md-4">
                                <kt-input
                                    type="date"
                                    label="Date Completed" 
                                    input-class="form-control form-control-sm"
                                    v-model="dateCompleted"
                                ></kt-input>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <!-- Billing & Payment Tab -->
            <div v-show="activeFormTab === 'billing'">
                <div class="row">
                    <div class="col-md-6">
                        <label><strong>Terms & Conditions</strong></label>
                        <textarea 
                            class="form-control" 
                            v-model="model.Terms"
                            rows="8"
                            placeholder="Payment terms, warranty info, etc."
                        ></textarea>
                    </div>
                    <div class="col-md-6">
                        <h5 class="mb-3"><strong>Invoice Totals</strong></h5>
                        <div class="row">
                            <div class="col-md-6">
                                <kt-input
                                    type="number"
                                    label="Subtotal" 
                                    input-class="form-control form-control-sm"
                                    placeholder="0.00"
                                    v-model="model.Subtotal"
                                    :step="0.01"
                                    readonly
                                ></kt-input>
                                <small class="text-muted">Calculated from line items</small>
                            </div>
                            <div class="col-md-6">
                                <kt-input
                                    type="number"
                                    label="Tax Amount" 
                                    input-class="form-control form-control-sm"
                                    placeholder="0.00"
                                    v-model="model.Tax"
                                    :step="0.01"
                                    readonly
                                ></kt-input>
                                <small class="text-muted">Calculated from line items</small>
                            </div>
                        </div>

                        <div class="row mt-3">
                            <div class="col-md-12">
                                <kt-input
                                    type="number"
                                    label="Total" 
                                    input-class="form-control form-control-sm"
                                    placeholder="0.00"
                                    v-model="model.Total"
                                    :step="0.01"
                                    readonly
                                ></kt-input>
                                <small class="text-muted">Subtotal + Tax</small>
                            </div>
                        </div>

                        <div class="row mt-3">
                            <div class="col-md-6">
                                <kt-input
                                    type="number"
                                    label="Amount Paid" 
                                    input-class="form-control form-control-sm"
                                    placeholder="0.00"
                                    v-model="model.AmountPaid"
                                    :step="0.01"
                                ></kt-input>
                            </div>
                            <div class="col-md-6">
                                <kt-input
                                    type="date"
                                    label="Date Paid" 
                                    input-class="form-control form-control-sm"
                                    v-model="datePaid"
                                ></kt-input>
                            </div>
                        </div>

                        <div class="row mt-3">
                            <div class="col-md-12">
                                <kt-input
                                    type="number"
                                    label="Balance Due" 
                                    input-class="form-control form-control-sm"
                                    placeholder="0.00"
                                    :model-value="balanceDue"
                                    :step="0.01"
                                    readonly
                                ></kt-input>
                                <small class="text-muted">Total - Amount Paid</small>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>

        <!-- Line Items and Assets Tabs (if work order exists) -->
        <div class="row mt-4" v-if="model.Id">
            <div class="col">
                <ul class="nav nav-tabs" role="tablist">
                    <li class="nav-item" role="presentation">
                        <button 
                            class="nav-link" 
                            :class="{ active: activeRelatedTab === 'items' }"
                            @click="activeRelatedTab = 'items'"
                            type="button"
                        >
                            <span class="badge" :class="activeRelatedTab === 'items' ? 'tab-badge-active' : 'tab-badge'">
                                <List :size="16" class="me-1" style="vertical-align: text-bottom;" />
                                Line Items
                            </span>
                        </button>
                    </li>
                    <li class="nav-item" role="presentation">
                        <button 
                            class="nav-link" 
                            :class="{ active: activeRelatedTab === 'assets' }"
                            @click="activeRelatedTab = 'assets'"
                            type="button"
                        >
                            <span class="badge" :class="activeRelatedTab === 'assets' ? 'tab-badge-active' : 'tab-badge'">
                                <Paperclip :size="16" class="me-1" style="vertical-align: text-bottom;" />
                                Assets
                            </span>
                        </button>
                    </li>
                </ul>

                <div class="tab-content border border-top-0 p-3 bg-light">
                    <!-- Line Items Tab -->
                    <div v-show="activeRelatedTab === 'items'">
                        <work-order-items-datatable
                            ref="itemsDatatableRef"
                            :work-order-id="model.Id"
                            :request-id="model.RequestId"
                            :client-id="model.ContactId"
                            :embedded="true"
                            @items-updated="handleItemsUpdated"
                        ></work-order-items-datatable>
                    </div>

                    <!-- Assets Tab -->
                    <div v-show="activeRelatedTab === 'assets'">
                        <kt-upload 
                            @process="processFiles($event)"
                            multiple="1"    
                        ></kt-upload>

                        <table class="table table-sm table-striped mt-3" v-if="files.length">
                            <thead>
                                <tr>
                                    <th style="width: 80px">Actions</th>
                                    <th>File Name</th>
                                    <th style="width: 120px">Type</th>
                                </tr>
                            </thead>
                            <tbody>
                                <tr v-for="file in files" :key="file.Id">
                                    <td>
                                        <button class="btn btn-sm btn-outline-danger" @click="showDelete(file)">
                                            <i class="fa fa-trash"></i>
                                        </button>
                                    </td>
                                    <td>
                                        <a :href="getDownloadUrl(file.Id)" target="_blank">
                                            {{ file.Title }}
                                        </a>
                                        <span v-if="AttachmentService.isFinalizedPDF(file)" class="badge bg-primary ms-2">Finalized PDF</span>
                                    </td>
                                    <td>
                                        <span class="text-muted">{{ file.Type }}</span>
                                    </td>
                                </tr>
                            </tbody>
                        </table>
                    </div>
                </div>
            </div>
        </div>

        <!-- Save prompt if no ID -->
        <div v-else class="alert alert-info mt-3">
            <i class="fas fa-info-circle"></i> &nbsp;
            <strong>Save the record first</strong> to add line items.
        </div>

        <!-- Email Compose Modal -->
        <email-compose
            :show="showEmailModal"
            :initial-data="emailData"
            @close="showEmailModal = false"
            @sent="handleEmailSent"
        ></email-compose>

        <!-- Finalize Work Order Modal -->
        <work-order-finalize-modal
            v-if="model.Id"
            :visible="showFinalizeModal"
            :work-order="model"
            @close="showFinalizeModal = false"
            @finalized="handleFinalizeComplete"
        ></work-order-finalize-modal>
    </div>
</template>

<script setup lang="ts">
import { ref, computed, watch, onMounted, onUnmounted } from 'vue';
import { useRoute } from 'vue-router';
import { WorkOrderModel } from '@/models/WorkOrderModel';
import ClientAc from '@/components/Clients/ClientAc.vue';
import JobAc from '@/components/Jobs/JobAc.vue';
import KtInput from '@/components/UI/KtInput.vue';
import KtSelect from '@/components/UI/KtSelect.vue';
import KtUpload from '@/components/UI/KtUpload.vue';
import WorkOrderItemsDatatable from '@/components/WorkOrders/WorkOrderItemsDatatable.vue';
import EmailCompose from '@/components/Email/EmailCompose.vue';
import WorkOrderFinalizeModal from '@/components/WorkOrders/WorkOrderFinalizeModal.vue';
import Helper from '@/services/Helper';
import InvoicePdfService from '@/services/InvoicePdfService';
import Alert from '@/services/Alert';
import Toast from '@/services/Toast';
import Api from '@/services/ApiService';
import AttachmentService from '@/services/AttachmentService';
import Events from '@/services/Emitter';
import { ClipboardList, DollarSign, List, Paperclip, RotateCcw } from 'lucide-vue-next';

const route = useRoute();
const activeFormTab = ref('header');
const activeRelatedTab = ref('items');
const showJobField = ref(false);
const files = ref<any[]>([]);
const showEmailModal = ref(false);
const emailData = ref<any>(null);
const showFinalizeModal = ref(false);
const itemsDatatableRef = ref(null);
let offJobCreated: Function | null = null;

const model = defineModel<WorkOrderModel>({
    default: () => new WorkOrderModel()
});

// Title options
const titleOptions = [
    { label: 'Invoice', value: 'Invoice' },
    { label: 'Partial', value: 'Partial' },
    /*
    { label: 'Estimate', value: 'Estimate' },
    { label: 'Request', value: 'Request' }
     */
];

// Status options
const statusOptions = [
    { label: 'Draft', value: 0 },
    { label: 'Pending', value: 1 },
    { label: 'Approved', value: 2 },
    { label: 'Completed', value: 3 },
    { label: 'Cancelled', value: 4 },
    { label: 'Finalized', value: 5 }
];

const paymentStatusOptions = [
    { label: 'Unpaid', value: 'Unpaid' },
    { label: 'Partial', value: 'Partial' },
    { label: 'Paid', value: 'Paid' }
];

// Date helpers (convert between Date objects and string format)
const dateCreated = computed({
    get: () => model.value.DateCreated || '',
    set: (val) => { model.value.DateCreated = val || null; }
});

const dateDue = computed({
    get: () => model.value.DateDue || '',
    set: (val) => { model.value.DateDue = val || null; }
});

const dateCompleted = computed({
    get: () => model.value.DateCompleted || '',
    set: (val) => { model.value.DateCompleted = val || null; }
});

const datePaid = computed({
    get: () => model.value.DatePaid || '',
    set: (val) => { model.value.DatePaid = val || null; }
});

// Computed balance due
const balanceDue = computed(() => {
    const total = model.value.Total || 0;
    const paid = model.value.AmountPaid || 0;
    return (total - paid).toFixed(2);
});

// Handle line items update (recalculate totals)
const handleItemsUpdated = (items: any[]) => {
    let subtotal = 0;
    let taxTotal = 0;

    items.forEach(item => {
        const lineTotal = (item.Quantity || 0) * (item.UnitCost || 0);
        const discount = item.DiscountAmount || 0;
        const taxableAmount = lineTotal - discount;
        const tax = item.TaxAmount || 0;

        subtotal += lineTotal - discount;
        taxTotal += tax;
    });

    // Update model with items array (needed for Print Invoice button visibility)
    model.value.Items = items;
    
    model.value.Subtotal = subtotal;
    model.value.Tax = taxTotal;
    model.value.Total = subtotal + taxTotal;
};

// Related Requests - for linking Invoice/Partial to a specific Request WorkOrder
const relatedRequests = ref<any[]>([]);

// Computed: is this an Invoice or Partial type?
const isInvoiceOrPartial = computed(() => {
    const title = model.value.Title?.toLowerCase();
    return title === 'invoice' || title === 'partial';
});

// Load Request-type WorkOrders linked to the same Job
const loadRelatedRequests = async () => {
    if (!model.value.RequestId) {
        relatedRequests.value = [];
        return;
    }
    try {
        const response = await Api.loadWorkOrders({
            RequestId: [model.value.RequestId],
            Title: 'Request',
            Page: 0,
            Size: 100
        });
        relatedRequests.value = response?.Data ?? [];
    } catch (error) {
        console.error('Error loading related requests:', error);
        relatedRequests.value = [];
    }
};

// Watch for client changes to clear the request selection
watch(() => model.value.ContactId, (newClient, oldClient) => {
    // If client changed and is different, clear the request
    if (oldClient !== undefined && newClient !== oldClient) {
        model.value.RequestId = null;
    }
});

// Reload related requests when RequestId changes
watch(() => model.value.RequestId, () => {
    if (isInvoiceOrPartial.value) {
        loadRelatedRequests();
    }
});

// Load related requests on mount if applicable
watch(() => model.value.Id, (newId) => {
    if (newId && isInvoiceOrPartial.value && model.value.RequestId) {
        loadRelatedRequests();
    }
}, { immediate: true });

// Print invoice function
const printInvoice = async () => {
    try {
        await InvoicePdfService.generateInvoicePDF(model.value);
        Toast.success("Invoice generated successfully");
    } catch (error) {
        console.error("Error generating invoice:", error);
        Alert.error("Failed to generate invoice PDF");
    }
};

// Open send email modal with prepopulated data
const openSendEmail = async () => {
    // Get client info for email recipient
    let clientEmail = '';
    if (model.value.ContactId) {
        try {
            const response = await Api.loadClient(model.value.ContactId);
            // Try to extract email from ContactInformation if available
            clientEmail = (response as any)?.Email || '';
        } catch (error) {
            console.error('Error loading client email:', error);
        }
    }

    // Generate PDF blob for attachment
    let pdfBlob: Blob | undefined;
    let pdfFileName: string | undefined;
    try {
        pdfBlob = await InvoicePdfService.generateInvoicePDFBlob(model.value);
        pdfFileName = `${model.value.Title || 'Invoice'}_${model.value.InvoiceNumber || 'Draft'}.pdf`;
    } catch (error) {
        console.error('Error generating PDF for email:', error);
    }

    // Prepare email subject with work order details
    const dateStr = model.value.DateCreated ? 
        new Date(model.value.DateCreated).toLocaleDateString() : 
        new Date().toLocaleDateString();
    
    const subject = `${model.value.Title || 'Invoice'} - ${model.value.InvoiceNumber || 'Draft'} - ${dateStr}`;
    
    const body = `Please find attached your ${model.value.Title || 'invoice'}.\n\n` +
                 `Invoice Number: ${model.value.InvoiceNumber || 'Pending'}\n` +
                 `Date: ${dateStr}\n` +
                 `Total: $${model.value.Total?.toFixed(2) || '0.00'}\n\n` +
                 `Thank you for your business!`;

    emailData.value = {
        to: clientEmail,
        subject: subject,
        body: body,
        attachments: [],
        pdfBlob: pdfBlob,
        pdfFileName: pdfFileName
    };

    showEmailModal.value = true;
};

const handleEmailSent = () => {
    Toast.success("Email sent successfully");
};

// Finalize modal
const openFinalizeModal = async () => {
    if (!model.value.Id) {
        Alert.notify({ text: 'Please save the work order first', type: 'warning' });
        return;
    }
    
    // Reload work order with items to ensure we have the latest data
    try {
        const fullWorkOrder = await Api.loadWorkOrder(model.value.Id);
        model.value = fullWorkOrder;
    } catch (error) {
        console.error('Error loading work order:', error);
    }
    
    showFinalizeModal.value = true;
};

const handleFinalizeComplete = async () => {
    showFinalizeModal.value = false;
    Toast.success('Work order finalized successfully');
    // Update the model status to finalized
    model.value.Status = 5;
    // Reload files to show the finalized PDF in Assets tab
    await loadFiles();
};

// File management
const processFiles = async (uploadedFiles: File[]) => {
    for (let i = 0; i < uploadedFiles.length; i++) {
        let file = uploadedFiles[i];
        await Api.uploadWorkOrderFile(file, model.value.Id!);
    }
    await loadFiles();
};

const loadFiles = async () => {
    if (model.value.Id) {
        files.value = await Api.loadWorkOrderFiles(model.value.Id);
    }
};

const showDelete = async (file: any) => {
    Alert.confirm("Are you sure you want to remove that file?", async () => {
        await Api.deleteWorkOrderFile(file.Id);
        await loadFiles();
    }, () => {});
};

// Restore deleted record
const restoreRecord = async () => {
    if (!model.value.Id) return;
    
    const success = await Api.restoreWorkOrder(model.value.Id);
    if (success) {
        model.value.IsDeleted = false;
        Toast.success('Record restored successfully');
    } else {
        Alert.notify('Failed to restore record');
    }
};

const getDownloadUrl = (fileId: string): string => {
    return AttachmentService.getDownloadUrl(fileId);
};

// Create Work Order button - opens JobEditForm with prepopulated data
const createWorkOrder = () => {
    // Prepare data to pass to JobEditForm
    const jobData = {
        ContactId: model.value.ContactId,
        Title: model.value.Title,
        Notes: model.value.InternalNotes,
        DateCreated: model.value.DateCreated,
        DateDue: model.value.DateDue,
        // Add any other relevant fields that should be prepopulated
    };
    
    // Emit event to open JobEditForm with prepopulated data
    Events.emit('job:edit', jobData);
};

// Listen for newly created job/request to link it back to this work order
const handleJobCreated = (jobId: number) => {
    if (!model.value.RequestId) {
        model.value.RequestId = jobId;
        showJobField.value = true;
    }
};

// Check if there are unsaved line items
const hasUnsavedItems = () => {
    console.log('[WorkOrderForm] Checking for unsaved items...');
    console.log('[WorkOrderForm] itemsDatatableRef.value:', itemsDatatableRef.value);
    
    if (!itemsDatatableRef.value) {
        console.log('[WorkOrderForm] No datatable ref, returning false');
        return false;
    }
    
    // Access the computed ref's value property
    const computedRef = itemsDatatableRef.value.hasUnsavedItems;
    console.log('[WorkOrderForm] computedRef:', computedRef);
    console.log('[WorkOrderForm] computedRef.value:', computedRef?.value);
    
    const hasUnsaved = computedRef?.value ?? false;
    console.log('[WorkOrderForm] hasUnsavedItems from datatable:', hasUnsaved);
    return hasUnsaved;
};

// Watch for changes to recalculate totals
watch(() => model.value.ContactId, (newVal, oldVal) => {
    if (newVal !== oldVal && newVal) {
        showJobField.value = true;
    }
});

// Expose hasUnsavedItems for parent component
defineExpose({
    hasUnsavedItems
});

// Watch for ID changes to load files
watch(() => model.value.Id, (newId) => {
    if (newId) {
        loadFiles();
    }
}, { immediate: true });

// Initialize from query params if creating new
onMounted(() => {
    if (route.query.requestId) {
        model.value.RequestId = Number(route.query.requestId);
    }
    if (route.query.clientId) {
        model.value.ContactId = Number(route.query.clientId);
    }
    
    // Set default dates for new work orders
    if (!model.value.Id) {
        model.value.DateCreated = Helper.dateToInput(new Date());
        model.value.Status = 0; // Draft
        model.value.PaymentStatus = 'Unpaid'; // Unpaid
    }
    
    // Listen for job created events
    offJobCreated = Events.on('job:created', handleJobCreated);
});

// Cleanup event listener
onUnmounted(() => {
    offJobCreated?.();
});
</script>

<style scoped>
.work-order-form {
    min-height: 400px;
}

/* Tab badge styling */
.tab-badge {
    background-color: #e9ecef;
    color: #495057;
    font-weight: 500;
    padding: 0.35em 0.65em;
}

.tab-badge-active {
    background-color: #3B9796;
    color: #fff;
    font-weight: 500;
    padding: 0.35em 0.65em;
}

.nav-link {
    border: none;
    background: transparent;
}

.nav-link:hover .tab-badge {
    background-color: #3B9796;
    color: #fff;
}
</style>
